import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Star, TrendingUp, Sparkles, Users, Clock, Filter, SortAsc } from 'lucide-react';
import { useData } from '../../context/DataContext';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import { generateBrandedProductSlug } from '../../utils/slugUtils';

// Container component
const Container = ({ children, className = "" }) => (
    <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const TrendingPage = () => {
    const { trendingType } = useParams();
    const { products, categories } = useData();
    const [sortBy, setSortBy] = useState('trending');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [brandFilter, setBrandFilter] = useState('all');

    // Trending page configurations
    const trendingConfigs = {
        'viral-trends': {
            title: 'Viral Trends',
            subtitle: 'We test what\'s blowing up online—so you buy smarter.',
            description: 'The products everyone\'s talking about, tested by our editors to separate the real deals from the hype.',
            icon: <TrendingUp className="h-6 w-6" />,
            badge: 'VIRAL',
            badgeColor: 'bg-red-100 text-red-800',
            getTrendingProducts: (products) => {
                // Products with high engagement, trending keywords, or viral status
                let filtered = products.filter(product => 
                    product.tags?.some(tag => 
                        typeof tag === 'string' && ['viral', 'trending', 'buzz', 'popular', 'hype'].includes(tag.toLowerCase())
                    ) || 
                    (product.name && product.name.toLowerCase().includes('viral')) ||
                    (product.description && product.description.toLowerCase().includes('trending'))
                );
                
                // Fallback: if no specific trending products, show top-rated products
                if (filtered.length === 0) {
                    filtered = products.filter(product => product.rating >= 3.5).slice(0, 12);
                }
                
                return filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            }
        },
        'ingredients': {
            title: 'Ingredient Deep Dives',
            subtitle: 'Science-backed breakdowns of powerhouse actives.',
            description: 'Products featuring the most researched and effective ingredients, with detailed explanations of why they work.',
            icon: <Sparkles className="h-6 w-6" />,
            badge: 'SCIENCE',
            badgeColor: 'bg-blue-100 text-blue-800',
            getTrendingProducts: (products) => {
                // Products with trending ingredients
                const trendingIngredients = [
                    'retinol', 'niacinamide', 'hyaluronic acid', 'vitamin c', 'salicylic acid',
                    'peptides', 'ceramides', 'squalane', 'bakuchiol', 'tranexamic acid',
                    'azelaic acid', 'glycolic acid', 'lactic acid', 'mandelic acid'
                ];
                
                let filtered = products.filter(product => 
                    trendingIngredients.some(ingredient => 
                        (product.name && product.name.toLowerCase().includes(ingredient)) ||
                        (product.description && product.description.toLowerCase().includes(ingredient)) ||
                        product.tags?.some(tag => typeof tag === 'string' && tag.toLowerCase().includes(ingredient))
                    )
                );
                
                // Fallback: if no ingredient-specific products, show skincare products
                if (filtered.length === 0) {
                    filtered = products.filter(product => {
                        const categoryName = typeof product.category === 'string' ? product.category : product.category?.name;
                        return categoryName && categoryName.toLowerCase().includes('skincare');
                    }).slice(0, 12);
                }
                
                return filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            }
        },
        'worth-the-hype': {
            title: 'Worth the Hype',
            subtitle: 'Our verdict on viral beauty—what\'s legit, what\'s not.',
            description: 'Products that lived up to their viral reputation, tested and verified by our editorial team.',
            icon: <Star className="h-6 w-6" />,
            badge: 'VERIFIED',
            badgeColor: 'bg-green-100 text-green-800',
            getTrendingProducts: (products) => {
                // High-rated products that are popular
                let filtered = products.filter(product => 
                    (product.rating >= 4.0) && 
                    (product.tags?.some(tag => 
                        typeof tag === 'string' && ['hype', 'viral', 'popular', 'recommended', 'editor choice'].includes(tag.toLowerCase())
                    ))
                );
                
                // Fallback: if no specific hype products, show top-rated products
                if (filtered.length === 0) {
                    filtered = products.filter(product => product.rating >= 4.0).slice(0, 12);
                }
                
                return filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            }
        },
        'editor-spotlights': {
            title: 'Editor Spotlights',
            subtitle: 'Our team\'s current obsessions across skin, hair, and makeup.',
            description: 'Hand-picked favorites from our editorial team, featuring products we\'re genuinely excited about.',
            icon: <Users className="h-6 w-6" />,
            badge: 'EDITOR\'S CHOICE',
            badgeColor: 'bg-purple-100 text-purple-800',
            getTrendingProducts: (products) => {
                // Editor-recommended products
                let filtered = products.filter(product => 
                    product.tags?.some(tag => 
                        typeof tag === 'string' && ['editor choice', 'staff pick', 'recommended', 'favorite'].includes(tag.toLowerCase())
                    )
                );
                
                // Fallback: if no editor picks, show diverse high-rated products
                if (filtered.length === 0) {
                    filtered = products.filter(product => product.rating >= 3.8).slice(0, 12);
                }
                
                return filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            }
        }
    };

    const config = trendingConfigs[trendingType];
    
    // Get unique brands from products
    const brands = useMemo(() => {
        if (!products || !Array.isArray(products)) return [];
        
        const brandSet = new Set();
        products.forEach(product => {
            if (product.brand && product.isActive) {
                brandSet.add(product.brand);
            }
        });
        
        return Array.from(brandSet).sort();
    }, [products]);
    
    // Get trending products based on configuration - moved outside conditional
    const trendingProducts = useMemo(() => {
        if (!config) return [];
        
        let filtered = config.getTrendingProducts(products);
        
        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(product => {
                const categoryName = typeof product.category === 'string' ? product.category : product.category?.name;
                return categoryName === categoryFilter;
            });
        }
        
        // Apply brand filter
        if (brandFilter !== 'all') {
            filtered = filtered.filter(product => {
                return product.brand === brandFilter;
            });
        }
        
        // Apply sorting
        switch (sortBy) {
            case 'price-low':
                return filtered.sort((a, b) => {
                    const priceA = parseFloat(a.price?.replace(/[^0-9.]/g, '') || 0);
                    const priceB = parseFloat(b.price?.replace(/[^0-9.]/g, '') || 0);
                    return priceA - priceB;
                });
            case 'price-high':
                return filtered.sort((a, b) => {
                    const priceA = parseFloat(a.price?.replace(/[^0-9.]/g, '') || 0);
                    const priceB = parseFloat(b.price?.replace(/[^0-9.]/g, '') || 0);
                    return priceB - priceA;
                });
            case 'rating':
                return filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            case 'trending':
            default:
                return filtered;
        }
    }, [products, config, sortBy, categoryFilter, brandFilter]);
    
    if (!config) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
                    <p className="text-gray-600 mb-4">The trending page you're looking for doesn't exist.</p>
                    <Link to="/" className="text-[#ab3d22] hover:text-[#8a2f1a] font-medium">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    const ProductCard = ({ product }) => {
        const [imageError, setImageError] = useState(false);
        const imageSrc = product.image || product.images?.[0];
        const imageAlt = product.name || 'Product image';

        return (
            <div className="bg-[#f8f8f8] rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-lg group">
                <div className="p-6">
                    {/* Product Image */}
                    <div className="aspect-square bg-white rounded-lg mb-4 flex items-center justify-center overflow-hidden p-4 border border-gray-100">
                        {imageError || !imageSrc ? (
                            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-400">
                                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                    <polyline points="21,15 16,10 5,21" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                            </div>
                        ) : (
                            <img
                                src={imageSrc}
                                alt={imageAlt}
                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-200"
                                onError={() => setImageError(true)}
                                loading="lazy"
                            />
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-3">
                        {/* Brand and Category */}
                        <div className="flex items-center gap-2">
                            {product.brand && (
                                <Link
                                    to={`/products?brand=${encodeURIComponent(product.brand)}`}
                                    className="text-xs uppercase tracking-wide text-gray-500 font-medium hover:text-[#ab3d22] transition-colors"
                                >
                                    {product.brand}
                                </Link>
                            )}
                            {product.category && (
                                <>
                                    {product.brand && <span className="text-gray-300">•</span>}
                                    <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                        {typeof product.category === 'string' ? product.category : product.category.name}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Product Name */}
                        <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-[#ab3d22] transition-colors">
                            {product.name}
                        </h3>

                        {/* Rating */}
                        {product.rating && (
                            <div className="flex items-center gap-1">
                                <div className="flex items-center">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            className={`h-4 w-4 ${
                                                i < Math.floor(product.rating)
                                                    ? 'text-[#ab3d22] fill-current'
                                                    : 'text-gray-300'
                                            }`}
                                        />
                                    ))}
                                </div>
                                {/* Review count temporarily hidden - will bring back later */}
                                <span className="text-sm text-gray-600">
                                    {product.rating}
                                </span>
                            </div>
                        )}

                        {/* TEMPORARILY HIDDEN - Price needs updating */}
                        {/* {product.price && (
                            <div className="text-lg font-bold text-gray-900">
                                {product.price}
                            </div>
                        )} */}

                        {/* CTA */}
                        <Link
                            to={product.affiliateLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-full px-4 py-2 bg-[#ab3d22] text-white text-sm font-medium rounded-lg hover:bg-[#8a2f1a] transition-colors"
                            data-cta="shop_now"
                            data-product={generateBrandedProductSlug(product.brand, product.name)}
                            data-category={typeof product.category === 'object' ? product.category?.slug : (product.category || '').toLowerCase().replace(/\s+/g, '-')}
                        >
                            Shop Now
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gray-50">
            <Navbar />
            
            {/* Header Hero */}
            <section className="bg-[#f8f8f8] border-b border-gray-200 py-8 sm:py-12">
                <Container>
                    <div className="text-center max-w-4xl mx-auto">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                {config.icon}
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.badgeColor}`}>
                                {config.badge}
                            </span>
                        </div>
                        
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Mollen, sans-serif', fontWeight: 600}}>
                            {config.title}
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-gray-600 mb-4 max-w-3xl mx-auto">
                            {config.subtitle}
                        </p>
                        
                        <p className="text-gray-600 max-w-3xl mx-auto">
                            {config.description}
                        </p>
                    </div>
                </Container>
            </section>

            {/* Filters and Sorting */}
            <div className="bg-[#f8f8f8] border-b border-gray-200">
                <Container>
                    <div className="py-4 sm:py-6">
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                            <div className="flex flex-wrap gap-4">
                                {/* Category Filter */}
                                <div className="flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-gray-500" />
                                    <select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent"
                                    >
                                        <option value="all">All Categories</option>
                                        {categories.map(category => (
                                            <option key={category._id} value={category.name}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Brand Filter */}
                                <select
                                    value={brandFilter}
                                    onChange={(e) => setBrandFilter(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent"
                                >
                                    <option value="all">All Brands</option>
                                    {brands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </select>

                            </div>

                            {/* Sort */}
                            <div className="flex items-center gap-2">
                                <SortAsc className="h-4 w-4 text-gray-500" />
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ab3d22] focus:border-transparent"
                                >
                                    <option value="trending">Trending</option>
                                    <option value="rating">Highest Rated</option>
                                    {/* TEMPORARILY HIDDEN - Price sorting options removed */}
                                    {/* <option value="price-low">Price: Low to High</option>
                                    <option value="price-high">Price: High to Low</option> */}
                                </select>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>

            {/* Products Grid */}
            <Container>
                <div className="py-8 sm:py-12">
                    {trendingProducts.length > 0 ? (
                        <>
                            <div className="flex items-center justify-between mb-8">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {trendingProducts.length} Trending Products
                                </h2>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Clock className="h-4 w-4" />
                                    <span>Updated daily</span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {trendingProducts.map((product) => (
                                    <ProductCard key={product._id} product={product} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-16">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No trending products found</h3>
                            <p className="text-gray-600 mb-6">
                                Try adjusting your filters or check back later for new trending items.
                            </p>
                            <button
                                onClick={() => {
                                    setCategoryFilter('all');
                                    setBrandFilter('all');
                                    setSortBy('trending');
                                }}
                                className="inline-flex items-center px-4 py-2 bg-[#ab3d22] text-white text-sm font-medium rounded-lg hover:bg-[#8a2f1a] transition-colors"
                            >
                                Clear Filters
                            </button>
                        </div>
                    )}
                </div>
            </Container>
            
            {/* The Radiance Difference */}
            <section className="bg-[#f8f8f8]">
                <Container className="py-8 sm:py-10">
                    <div className="flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto">
                        <div className="flex justify-center lg:w-1/2 mb-8 lg:mb-0 lg:pr-8">
                            <img
                                src="/images/radiancedifference.png"
                                alt="Woman with radiant, glowing skin applying skincare"
                                className="w-full max-w-[450px] h-auto rounded-3xl"
                            />
                        </div>
                        <div className="lg:w-1/2 text-center lg:text-left">
                            <div className="mb-4 text-sm tracking-widest text-gray-500 uppercase">NEWSLETTER</div>
                            <h2 className="text-3xl md:text-4xl mb-6" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                                <span className="text-gray-900">The Radiance</span> <span className="text-[#ab3d22]">Difference</span>
                            </h2>
                            <div className="space-y-6 max-w-2xl mx-auto lg:mx-0">
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    The internet is filled with praise for every product—but which ones actually work? Many users share your frustration with unreliable reviews, paid promotions, and information overload.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Choosing beauty and wellness products should be simple and trustworthy—not a source of stress.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Radiance is your go-to hub for beauty solutions that genuinely work—for everyone.
                                </p>
                                <a 
                                    href="http://eepurl.com/jpTPXQ"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block"
                                >
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-semibold text-white bg-[#ab3d22] hover:bg-[#8a2f1a] focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 transition-all duration-200"
                                    >
                                        Subscribe Now
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <Footer />
        </div>
    );
};

export default TrendingPage;
